import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
/* Utils */
import StoryboardRenderingUtil from 'Utils/content/storyboard-rendering-util';
import StoryboardSkeletonUtil from 'Utils/content/storyboard-skeleton-util';
import { USAGE_EVENT_NAMES } from 'Utils/analytics/usage';
import { SPACING } from 'Style/spacing';
import { COLORS, TEXT_COLORS } from 'Style/colors';
import { UI_HEADING_TYPES, UI_TEXT_TYPES } from 'Style/typography';
import { BREAKPOINTS } from 'Style/breakpoints';
import { styleOnlyProps } from 'Style/style-helpers';

/* Components */
import StoryboardPost from './post';
import StoryboardIntro from './storyboard-intro';
import WebLink from '../../components/web-link';

const { sizes } = StoryboardRenderingUtil;

const StyledStoryBoardGroupTopic = styled(WebLink)({
  background: COLORS.red,
  margin: `${SPACING.MEDIUM} ${SPACING.MEDIUM} ${SPACING.MEDIUM} 0px`,
  padding: `${SPACING.SMALL} ${SPACING.MEDIUM}`,
  textTransform: 'uppercase',
  fontWeight: '$font-weight--black',
});

function renderTopic(groupTopic) {
  const { topic, topicTag } = groupTopic;
  return (
    <StyledStoryBoardGroupTopic
      href={`/topic/${topicTag}`}
      navFromEventName={USAGE_EVENT_NAMES.SECTION_ENTER}
    >
      #{topic}
    </StyledStoryBoardGroupTopic>
  );
}

const STORYBOARD_GROUP_SMALL = {
  gridTemplateColumns: '1fr',
  ...BREAKPOINTS.tabletLandscapeUp({
    gridTemplateColumns: `repeat(2, calc(50% - (${SPACING.XLARGE} / 2)))`,
    gridTemplateAreas: '"header header"',
  }),
};

const STORYBOARD_GROUP_MEDIUM = {
  gridTemplateColumns: '1fr',
  ...BREAKPOINTS.tabletPortraitUp({
    gridTemplateColumns: `repeat(2, calc(50% - (${SPACING.XLARGE} / 2)))`,
    gridTemplateAreas: '"header header"',
  }),
  ...BREAKPOINTS.desktopUp({
    gridTemplateColumns: `repeat(3, calc((100% / 3) -  (${SPACING.XLARGE} / 3)))`,
    gridTemplateAreas: '"header header header"',
  }),
};

const getStoryBoardSizeStyle = (size) => {
  if (size === sizes.SMALL) {
    return STORYBOARD_GROUP_SMALL;
  } else if (size === sizes.MEDIUM) {
    return STORYBOARD_GROUP_MEDIUM;
  }
};

const StyledStoryBoardGroupTopicsContainer = styled.div({
  marginTop: SPACING.XLARGE,
});

export const StyledStoryBoardGroupHeader = styled(
  'header',
  styleOnlyProps('isInline'),
)(({ isInline }) => ({
  gridArea: 'header',
  marginTop: 0,
  marginBottom: SPACING.BASE,
  h2: {
    ...UI_HEADING_TYPES.MEDIUM,
    color: isInline ? 'unset' : TEXT_COLORS.overlay,
  },
}));

const StyledSection = styled.section(
  BREAKPOINTS.tabletPortraitUp({
    display: 'grid',
    gridColumnGap: SPACING.XLARGE,
    gridRowGap: SPACING.XLARGE,
    gridTemplateAreas: '"header"',
  }),
  // Divider between two items within a storyboard group/section
  BREAKPOINTS.phone({
    '&>div+div': {
      borderTop: '2px solid',
    },
  }),
  (props) => ({
    paddingTop: SPACING.XLARGE,
    ...props?.typeSize,
    '>*': {
      '@media (max-width: 992px)': {
        paddingLeft: SPACING.LARGE,
        paddingRight: SPACING.LARGE,
      },
    },
  }),
);

const StoryBoardGroupSubTitle = styled(
  'p',
  styleOnlyProps('isInline'),
)(
  ({ isInline }) => (
    UI_TEXT_TYPES.SUPPORTING,
    {
      color: isInline ? 'unset' : TEXT_COLORS.secondaryReverse,
    }
  ),
);

function StoryboardGroup({
  section,
  group,
  styleOverride,
  postStyleOverride,
  isInline,
}) {
  const { items } = group;

  const title = StoryboardSkeletonUtil.getCustomizedTitle(group);
  const subtitle = StoryboardSkeletonUtil.getCustomizedText(group);
  const topics = StoryboardRenderingUtil.groupTopics(group);
  const size = StoryboardRenderingUtil.groupSize(group);

  const typeSize = getStoryBoardSizeStyle(size);

  // Render intro group itemss using StoryboardIntro
  const renderItem = (item, index) => {
    if (StoryboardRenderingUtil.isIntroGroup(group)) {
      return <StoryboardIntro key={item.id} item={item} />;
    }

    return (
      <StoryboardPost
        key={item.id}
        item={item}
        section={section}
        group={group}
        index={index}
        postStyleOverride={postStyleOverride}
        isInline={isInline}
      />
    );
  };

  return (
    <StyledSection
      typeSize={typeSize}
      className="storyboard-group"
      style={styleOverride}
    >
      {(title || subtitle) && (
        <StyledStoryBoardGroupHeader isInline={isInline}>
          {title && <h2>{title}</h2>}
          {subtitle && (
            <StoryBoardGroupSubTitle isInline={isInline}>
              {subtitle}
            </StoryBoardGroupSubTitle>
          )}
        </StyledStoryBoardGroupHeader>
      )}
      {topics && (
        <StyledStoryBoardGroupTopicsContainer>
          {topics.map(renderTopic)}
        </StyledStoryBoardGroupTopicsContainer>
      )}
      {items.map(renderItem)}
    </StyledSection>
  );
}

StoryboardGroup.propTypes = {
  section: PropTypes.object.isRequired,
  group: PropTypes.object.isRequired,
  styleOverride: PropTypes.object,
  postStyleOverride: PropTypes.object,
  isInline: PropTypes.bool,
};

StoryboardGroup.defaultProps = {};

export default StoryboardGroup;
